import { decodeJwtPayload, isJwtPayloadExpired } from './jwt';

export const PARTNER_SSO_SOURCE = 'partner' as const;
export const LEGACY_PARTNER_SSO_SOURCE = 'web_partner_sso' as const;
export const DEVSTACK_BLR_PULSE_PARTNER_MERCHANT_ID = 'BLRPulseMID01';
const DEVSTACK_FALLBACK_ENVS = new Set(['development', 'devstack']);

export type PartnerSsoSource =
  | typeof PARTNER_SSO_SOURCE
  | typeof LEGACY_PARTNER_SSO_SOURCE;

export type PartnerNotes = Record<string, string>;

export interface BlrPulseSsoJwtClaims extends Record<string, unknown> {
  sub?: string;
  user_id?: string;
  phone?: string;
  partner_merchant_id?: string;
  source?: string;
  notes?: PartnerNotes;
  callback_url?: string;
  iat: number;
  exp: number;
}

// One level of the city -> terminal -> terminal type -> exact lounge hierarchy
// Pulse can pass at SSO login. All levels are optional and independent - Pulse
// may send any subset.
export interface PartnerLocationFilters {
  terminalName?: string;
  terminalType?: string;
  loungeId?: string;
}

export interface PartnerSessionMetadata {
  partnerId: string;
  source: PartnerSsoSource;
  notes?: PartnerNotes;
  city?: string;
  locationFilters?: PartnerLocationFilters;
  // Post-booking native app redirect target from the JWT `callback_url` claim.
  // Treated as untrusted input on the FE (see the scheme allowlist in
  // utils/partnerCallback.ts) - may already contain partner query params
  // (e.g. `order_id`), which are preserved except for the keys we reserve
  // (`booking_id`, `transaction_id`, `status`). Only ever redirected to on a
  // successfully confirmed booking.
  callbackUrl?: string;
}

export type PartnerTokenResult =
  | {
      status: 'partner';
      token: string;
      claims: BlrPulseSsoJwtClaims;
      partnerSession: PartnerSessionMetadata;
    }
  | {
      status: 'non_partner';
      token: string;
      claims: BlrPulseSsoJwtClaims;
    }
  | {
      status: 'invalid' | 'expired';
      token: string;
    };

export interface PartnerSsoLaunchContext {
  city?: string;
  locationFilters?: PartnerLocationFilters;
}

const isStringRecord = (value: unknown): value is PartnerNotes => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  return Object.values(value).every((entry) => typeof entry === 'string');
};

const normalizeNotes = (notes: unknown): PartnerNotes | undefined => {
  return isStringRecord(notes) ? notes : undefined;
};

const normalizeCallbackUrl = (callbackUrl: unknown): string | undefined => {
  return typeof callbackUrl === 'string' && callbackUrl.trim()
    ? callbackUrl.trim()
    : undefined;
};

const isDevstackPartnerFallbackEnabled = (): boolean => {
  const appEnv = process.env.VITE_APP_ENV || process.env.NODE_ENV || '';
  return DEVSTACK_FALLBACK_ENVS.has(appEnv);
};

const getPartnerMerchantId = (
  claims: BlrPulseSsoJwtClaims,
): string | undefined => {
  const partnerMerchantId = claims.partner_merchant_id?.trim();

  if (partnerMerchantId) {
    return partnerMerchantId;
  }

  // TODO(BLR Pulse): remove this devstack fallback once OAuth integration
  // populates partner_merchant_id in the SSO JWT.
  return isDevstackPartnerFallbackEnabled()
    ? DEVSTACK_BLR_PULSE_PARTNER_MERCHANT_ID
    : undefined;
};

const isPartnerSsoSource = (
  source: string | undefined,
): source is PartnerSsoSource => {
  return source === PARTNER_SSO_SOURCE || source === LEGACY_PARTNER_SSO_SOURCE;
};

export const parsePartnerSsoToken = (
  token: string,
  context: PartnerSsoLaunchContext = {},
): PartnerTokenResult => {
  const claims = decodeJwtPayload<BlrPulseSsoJwtClaims>(token);

  if (!claims) {
    return { status: 'invalid', token };
  }

  if (isJwtPayloadExpired(claims)) {
    return { status: 'expired', token };
  }

  if (!isPartnerSsoSource(claims.source)) {
    return { status: 'non_partner', token, claims };
  }

  const partnerMerchantId = getPartnerMerchantId(claims);

  if (!partnerMerchantId) {
    return { status: 'non_partner', token, claims };
  }

  return {
    status: 'partner',
    token,
    claims,
    partnerSession: {
      partnerId: partnerMerchantId,
      source: claims.source,
      city: context.city,
      locationFilters: context.locationFilters,
      notes: normalizeNotes(claims.notes),
      callbackUrl: normalizeCallbackUrl(claims.callback_url),
    },
  };
};

const safeParseUrl = (href: string): URL | null => {
  try {
    return new URL(href);
  } catch {
    return null;
  }
};

const getSearchParam = (href: string, key: string): string | null => {
  const value = safeParseUrl(href)?.searchParams.get(key)?.trim();
  return value || null;
};

export const getSsoTokenFromUrl = (href: string): string | null =>
  getSearchParam(href, 'token');

export const getSsoCodeFromUrl = (href: string): string | null =>
  getSearchParam(href, 'code');

export const getSsoCityFromUrl = (href: string): string | undefined =>
  getSearchParam(href, 'city') ?? undefined;

export const hasLocationFilters = (filters?: PartnerLocationFilters): boolean =>
  Boolean(filters && Object.values(filters).some(Boolean));

// Single source of truth for the fallback banner and its telemetry event,
// so the two conditions can't drift apart.
export const isLocationFilterFallback = (
  filters: PartnerLocationFilters | undefined,
  filtersApplied: boolean,
  hasLounges: boolean,
): boolean => hasLocationFilters(filters) && !filtersApplied && hasLounges;

export const getLocationFiltersFromSearchParams = (
  searchParams: URLSearchParams,
): PartnerLocationFilters | undefined => {
  const filters: PartnerLocationFilters = {
    terminalName: searchParams.get('terminal_name')?.trim() || undefined,
    terminalType: searchParams.get('terminal_type')?.trim() || undefined,
    loungeId: searchParams.get('lounge_id')?.trim() || undefined,
  };

  return hasLocationFilters(filters) ? filters : undefined;
};

export const getLocationFiltersFromUrl = (
  href: string,
): PartnerLocationFilters | undefined => {
  const url = safeParseUrl(href);
  return url ? getLocationFiltersFromSearchParams(url.searchParams) : undefined;
};

export const stripTokenFromUrl = (href: string): string => {
  const url = safeParseUrl(href);
  if (!url) {
    return href;
  }

  url.searchParams.delete('code');
  url.searchParams.delete('token');
  const noteParamKeys: string[] = [];
  url.searchParams.forEach((_, key) => {
    if (key === 'notes' || key.startsWith('notes[')) {
      noteParamKeys.push(key);
    }
  });
  noteParamKeys.forEach((key) => url.searchParams.delete(key));

  return `${url.pathname}${url.search}${url.hash}`;
};
